import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { supabase, apiFetch } from "@/lib/supabase";

const AuthContext = createContext(null);

/**
 * SUPABASE JS v2 + REACT.STRICTMODE COMPATIBILITY
 * ------------------------------------------------
 * Supabase JS v2 acquires a `navigator.locks` lock inside `getSession()` and
 * inside its internal token refresh scheduler. Under React.StrictMode (which
 * mounts every effect twice in dev), two competing getSession() calls end up
 * deadlocked on that lock and the app hangs on the initial "Loading…" spinner.
 *
 * The Supabase-recommended pattern is: DO NOT call getSession() manually on
 * mount. Instead, subscribe to `onAuthStateChange` — Supabase fires an
 * INITIAL_SESSION event exactly once with the current session (or null) as
 * soon as it has finished restoring from storage. That callback does NOT
 * contend for the lock and works reliably under StrictMode.
 *
 * We also keep a 4s safety timeout: if the INITIAL_SESSION event never fires
 * (shouldn't happen, but defensive), the app still boots into the logged-out
 * state so the user is never stuck on a spinner.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState(() => localStorage.getItem("cd_org_id") || null);
  const refreshInFlight = useRef(false);

  const refresh = useCallback(async ({ retry = 1 } = {}) => {
    if (refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      const me = await apiFetch("/auth/me");
      setProfile(me.profile);
      setMemberships(me.memberships || []);
      setMembershipsLoaded(true);
      const stored = localStorage.getItem("cd_org_id");
      const has = (me.memberships || []).find((m) => m.organization_id === stored);
      if (!has && (me.memberships || []).length) {
        const first = me.memberships[0].organization_id;
        setCurrentOrgId(first);
        localStorage.setItem("cd_org_id", first);
      } else if (has) {
        setCurrentOrgId(stored);
      } else {
        setCurrentOrgId(null);
        localStorage.removeItem("cd_org_id");
      }
    } catch (e) {
      if (e.status === 401) {
        setProfile(null);
        setMemberships([]);
        setMembershipsLoaded(true);
      } else if (e.status === 403 && String(e.message).includes("email_not_verified")) {
        setProfile(null);
        setMemberships([]);
        setMembershipsLoaded(true);
      } else if (retry > 0) {
        refreshInFlight.current = false;
        await new Promise((r) => setTimeout(r, 700));
        return refresh({ retry: retry - 1 });
      } else {
        console.warn("auth/me failed (keeping prior state):", e.message);
      }
    } finally {
      refreshInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    // Belt-and-suspenders safety: if INITIAL_SESSION never fires (shouldn't
    // happen), flip `initialized` after 4s so the app boots into a logged-out
    // state rather than a permanent spinner. Fires UNCONDITIONALLY — no cleanup
    // guard, no `mounted` check — that's the whole point of a safety net.
    const safety = setTimeout(() => {
      setInitialized(true);
      setMembershipsLoaded(true);
    }, 4000);

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      setSession(sess);

      if (event === "INITIAL_SESSION") {
        clearTimeout(safety);
        if (sess) {
          await refresh();
        } else {
          setMembershipsLoaded(true);
        }
        setInitialized(true);
      } else if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        await refresh();
        setInitialized(true);
      } else if (event === "SIGNED_OUT") {
        setProfile(null);
        setMemberships([]);
        setMembershipsLoaded(true);
        setCurrentOrgId(null);
        localStorage.removeItem("cd_org_id");
        setInitialized(true);
      }
      // TOKEN_REFRESHED / PASSWORD_RECOVERY → session already set above; no refetch.
    });

    return () => {
      clearTimeout(safety);
      sub.subscription.unsubscribe();
    };
  }, [refresh]);

  const switchOrg = (orgId) => {
    setCurrentOrgId(orgId);
    localStorage.setItem("cd_org_id", orgId);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const currentMembership = memberships.find((m) => m.organization_id === currentOrgId);
  const currentOrg = currentMembership?.organizations || null;
  const currentRole = currentMembership?.role || null;
  const emailVerified = !!session?.user?.email_confirmed_at;

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user || null,
        profile,
        memberships,
        membershipsLoaded,
        currentOrg,
        currentOrgId,
        currentRole,
        emailVerified,
        loading: !initialized, // legacy alias for existing pages (Setup.jsx etc)
        initialized,
        refresh,
        switchOrg,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
